import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../components/auth/AuthContext';

interface SearchHistoryItem {
  id: string;
  query: string;
  searches: number;
  likes: number;
  lastUsedAt: string;
}

export const useUserHistory = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: searchHistory = [], isLoading } = useQuery<SearchHistoryItem[]>(
    ['userHistory', user?.id],
    async () => {
      if (!user?.id) return [];

      const { data: history } = await supabase
        .from('user_search_history')
        .select(`
          id,
          last_used_at,
          searches:search_id (
            id,
            query,
            searches,
            likes
          )
        `)
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false });

      return (history || []).map(item => ({
        id: item.searches.id,
        query: item.searches.query,
        searches: item.searches.searches,
        likes: item.searches.likes,
        lastUsedAt: item.last_used_at
      }));
    },
    {
      enabled: !!user?.id,
      staleTime: 0, // Disable stale time to always fetch fresh data
      cacheTime: 5 * 60 * 1000
    }
  );

  const { mutate: recordSearch } = useMutation(
    async (query: string) => {
      if (!user?.id) return null;

      // First get or create the search
      const { data: searchData } = await supabase
        .from('searches')
        .select()
        .eq('query', query)
        .maybeSingle();

      let searchId;
      if (searchData) {
        searchId = searchData.id;
        // Update search count
        await supabase
          .from('searches')
          .update({ 
            searches: (searchData.searches || 0) + 1,
            last_searched_at: new Date().toISOString()
          })
          .eq('id', searchId);
      } else {
        // Create new search
        const { data: newSearch } = await supabase
          .from('searches')
          .insert({ query })
          .select()
          .single();
        
        if (newSearch) {
          searchId = newSearch.id;
        }
      }

      if (searchId) {
        // Update or create user search history
        await supabase
          .from('user_search_history')
          .upsert({
            user_id: user.id,
            search_id: searchId,
            last_used_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,search_id'
          });
      }

      return searchId;
    },
    {
      onSuccess: () => {
        if (user?.id) {
          // Invalidate affected queries to ensure data is fresh
          queryClient.invalidateQueries(['userHistory', user.id]);
          queryClient.invalidateQueries(['marketSearches', user.id]);
          queryClient.invalidateQueries('searches');
        }
      }
    }
  );

  // Update search history when likes change
  const updateSearchLikes = (searchId: string, newLikeCount: number) => {
    queryClient.setQueryData<SearchHistoryItem[]>(
      ['userHistory', user?.id],
      (oldData = []) => {
        return oldData.map(item => {
          if (item.id === searchId) {
            return {
              ...item,
              likes: newLikeCount
            };
          }
          return item;
        });
      }
    );
  };

  return {
    searchHistory,
    recordSearch,
    updateSearchLikes,
    isLoading
  };
};